import { Box } from "orcs-design-system";
import React, { useRef, useEffect, memo } from "react";
import PropTypes from "prop-types";
import { isEmpty } from "lodash";

const BANNER_HEIGHT = 90;

const BannerImage = ({
  imageUrl,
  cropData,
  isCollapsed,
  bannerHeight = BANNER_HEIGHT,
}) => {
  const canvasRef = useRef(null);
  const activeRequestRef = useRef(null);
  const lastDrawKeyRef = useRef(null);

  useEffect(() => {
    if (!imageUrl || isEmpty(cropData) || !canvasRef.current) {
      return;
    }

    const { x, y, width, height } = cropData;
    if (
      [x, y, width, height].some(
        (v) => typeof v !== "number" || Number.isNaN(v)
      ) ||
      width <= 0 ||
      height <= 0
    ) {
      return;
    }

    const drawKey = `${imageUrl}|${x},${y},${width},${height}`;
    if (lastDrawKeyRef.current === drawKey) {
      return;
    }

    const requestId = Symbol("banner-image");
    activeRequestRef.current = requestId;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // Clear immediately to avoid showing stale image
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    canvas.width = width;
    canvas.height = height;

    const img = new Image();
    img.crossOrigin = "anonymous";

    const safeDraw = () => {
      if (activeRequestRef.current !== requestId) {
        return;
      }
      const sx = Math.max(0, Math.min(x, img.naturalWidth));
      const sy = Math.max(0, Math.min(y, img.naturalHeight));
      const sw = Math.max(0, Math.min(width, img.naturalWidth - sx));
      const sh = Math.max(0, Math.min(height, img.naturalHeight - sy));
      if (sw === 0 || sh === 0) {
        return;
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      try {
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height);
        lastDrawKeyRef.current = drawKey;
      } catch {
        // Silent fail
      }
    };

    img.onload = () => safeDraw();
    img.onerror = () => {
      if (activeRequestRef.current === requestId) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };

    img.src = imageUrl;

    return () => {
      // Invalidate this request
      if (activeRequestRef.current === requestId) {
        activeRequestRef.current = null;
      }
    };
  }, [
    imageUrl,
    cropData?.x,
    cropData?.y,
    cropData?.width,
    cropData?.height,
    isCollapsed,
    bannerHeight,
    cropData,
  ]);

  const height = isCollapsed ? 10 : bannerHeight;

  return (
    <Box width="100%">
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          height: `${height}px`,
          display: "block",
          objectFit: "cover",
        }}
      />
    </Box>
  );
};

BannerImage.propTypes = {
  imageUrl: PropTypes.string.isRequired,
  cropData: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }).isRequired,
  isCollapsed: PropTypes.bool,
  bannerHeight: PropTypes.number,
};

export default memo(BannerImage);
